import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  DialogActions,
  Divider,
  Button,
  makeStyles
} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import moment from 'moment';
import { getDateValues } from './utils';

const useStyles = makeStyles({
  picker: {
    '& .MuiPickersBasePicker-pickerView': {
      width: '100%',
      maxWidth: 'none'
    }
  },
  weekList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5em',
    padding: '0 1em 1em'
  }
});

const normalizeWeekStartDay = weekStartDay => {
  if (!weekStartDay) return 1; // default ISO Monday

  // number: 1–7
  if (Number.isInteger(weekStartDay)) {
    return Math.min(Math.max(weekStartDay, 1), 7);
  }

  // string: mon, monday, sun, etc
  if (typeof weekStartDay === 'string') {
    const key = weekStartDay.toLowerCase().slice(0, 3);

    const map = {
      mon: 1,
      tue: 2,
      wed: 3,
      thu: 4,
      fri: 5,
      sat: 6,
      sun: 7
    };

    return map[key] ?? 1;
  }

  return 1;
};

const getWeeksInMonth = (date, weekStartDay) => {
  const startOfMonth = moment(date).startOf('month');
  const endOfMonth = moment(date).endOf('month');
  const monthName = startOfMonth.format('MMMM');

  const startDay = normalizeWeekStartDay(weekStartDay);

  const weeks = [];
  let cursor = startOfMonth.clone().isoWeekday(startDay);
  if (cursor.isAfter(startOfMonth)) {
    cursor.subtract(1, 'week');
  }

  let weekIndex = 1;

  while (cursor.isBefore(endOfMonth)) {
    const weekStart = cursor.clone().isoWeekday(startDay);
    const weekEnd = cursor.clone().isoWeekday(startDay + 6);

    weeks.push({
      key: `${startOfMonth.format('YYYY-MM')}-w${weekIndex}`,
      label: `${monthName} Week ${weekIndex}`,
      start: weekStart,
      end: weekEnd
    });

    cursor.add(1, 'week');
    weekIndex++;
  }

  return weeks;
};

const Week = ({
  handleChangeDatePickerValue,
  handleCloseDialog,
  dateValues,
  type,
  defaultDatePicker,
  prefixText,
  ...data
}) => {
  const { frontEnd = {} } = useSelector(state => state.bizMeta);
  const classes = useStyles();
  const [month, setMonth] = useState(moment().format('YYYY-MM-DD'));
  const [selectedWeek, setSelectedWeek] = useState(null);
  const weekStartDay = frontEnd?.datePicker?.weekStartDay ?? '';

  const weeks = useMemo(() => getWeeksInMonth(month, weekStartDay), [month]);

  const handleMonthChange = val => {
    setMonth(moment(val).format('YYYY-MM-DD'));
    setSelectedWeek(null);
  };

  const handleOk = () => {
    if (!selectedWeek) return;

    handleChangeDatePickerValue(
      getDateValues.week({
        values: {
          dt1: selectedWeek.start.format('YYYY-MM-DD'),
          dt2: selectedWeek.end.format('YYYY-MM-DD'),
          textValue: selectedWeek.label
        },
        prefixText: prefixText || 'week'
      })
    );

    handleCloseDialog();
  };

  return (
    <>
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <Box display="flex" width="100%" gap="2em">
          {/* Month picker on the left */}
          <Box flex={1} className={classes.picker}>
            <DatePicker
              label="Select Month"
              openTo="month"
              views={['month']}
              variant="static"
              orientation="landscape"
              value={month}
              onChange={handleMonthChange}
              autoOk
            />
          </Box>

          {/* Weeks on the right */}
          <Box flex={1} className={classes.weekList}>
            {weeks.map(w => (
              <Button
                key={w.key}
                fullWidth
                variant={selectedWeek?.key === w.key ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => setSelectedWeek(w)}
              >
                {w.label}: {w.start.format('MMM DD')} – {w.end.format('MMM DD')}
              </Button>
            ))}
          </Box>
        </Box>
      </MuiPickersUtilsProvider>

      <DialogActions style={{ justifyContent: 'space-between' }}>
        <Button color="primary" onClick={handleCloseDialog}>
          Cancel
        </Button>

        <Button
          color="primary"
          variant="contained"
          onClick={handleOk}
          disabled={!selectedWeek}
        >
          Ok
        </Button>
      </DialogActions>
    </>
  );
};

export default Week;
